"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef } from "react";
import { ReadOnlyCarePlanCard } from "@/components/care-plan/read-only-care-plan-card";
import { cx } from "@/lib/tailwind-utils";
import type { CarePlanDetail } from "@/types/care-plan";
import { PatientCarePlanViewer } from "./patient-care-plan-viewer";
import { CarePlanPrintDialog } from "./print/care-plan-print-dialog";
import { useCarePlanPrintFlow } from "./print/use-care-plan-print-flow";
import { StepList } from "./step-list";
import { type CarePlanSize, type PatientCarePlanStep, StepType, isLinkedMessage } from "./types";
import { toPatientSteps } from "./types";

/**
 * Recursively collect all non-MESSAGE steps from the step tree,
 * descending into resolved branches, to find the first upcoming step.
 */
function collectTimelineSteps(steps: PatientCarePlanStep[]): PatientCarePlanStep[] {
  const result: PatientCarePlanStep[] = [];
  for (const step of steps) {
    if (step.type === StepType.DECISION) {
      for (const branch of step.branches ?? []) {
        if (branch.selectedDate) {
          result.push(...collectTimelineSteps(branch.steps.filter((s): s is PatientCarePlanStep => s.kind === "patient")));
        }
      }
    } else if (!isLinkedMessage(step)) {
      result.push(step);
    }
  }
  return result;
}
interface ReadOnlyCarePlanProps {
  carePlan: CarePlanDetail;
  patientId: string;
  orientation?: "horizontal" | "vertical";
  size?: CarePlanSize;
}
export function ReadOnlyCarePlan(t0) {
  const $ = _c(26);
  const {
    carePlan,
    patientId,
    orientation: t1,
    size: t2
  } = t0;
  const orientation = t1 === undefined ? "horizontal" : t1;
  const size = t2 === undefined ? "md" : t2;
  let t3;
  if ($[0] !== carePlan.steps) {
    t3 = toPatientSteps(carePlan.steps);
    $[0] = carePlan.steps;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const steps = t3;
  const t4 = steps.length > 0;
  let t5;
  if ($[2] !== carePlan.name || $[3] !== t4) {
    t5 = {
      name: carePlan.name,
      hasSteps: t4
    };
    $[2] = carePlan.name;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const {
    state: t6,
    actions: t7
  } = useCarePlanPrintFlow(t5);
  const {
    dialogOpen: printDialogOpen,
    isPrinting
  } = t6;
  const {
    openDialog: handlePrint,
    onOpenChange,
    confirmPrint: handlePrintConfirm
  } = t7;
  const scrollContainerRef = useRef(null);
  let t8;
  if ($[5] !== steps) {
    const timelineSteps = collectTimelineSteps(steps);
    t8 = timelineSteps.find(_temp);
    $[5] = steps;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const upcoming = t8;
  const upcomingStepId = upcoming?.id ?? null;
  let t10;
  let t9;
  if ($[7] !== upcomingStepId) {
    t9 = () => {
      const container = scrollContainerRef.current;
      if (!container || !upcomingStepId) {
        return;
      }
      const timer = setTimeout(() => {
        const target = container.querySelector(`[data-step-id="${upcomingStepId}"]`);
        if (target) {
          const containerRect = container.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          const offset = targetRect.left - containerRect.left - (containerRect.width - targetRect.width) / 2;
          container.scrollBy({
            left: offset,
            behavior: "smooth"
          });
        }
      }, 300);
      return () => clearTimeout(timer);
    };
    t10 = [upcomingStepId];
    $[7] = upcomingStepId;
    $[8] = t10;
    $[9] = t9;
  } else {
    t10 = $[8];
    t9 = $[9];
  }
  useEffect(t9, t10);
  let t11;
  if ($[10] !== carePlan.id || $[11] !== handlePrintConfirm || $[12] !== isPrinting || $[13] !== onOpenChange || $[14] !== patientId || $[15] !== printDialogOpen || $[16] !== size || $[17] !== steps.length) {
    t11 = contentClassName => <div className={cx(contentClassName, "text-left")} ref={scrollContainerRef}>{steps.length > 0 && <PatientCarePlanViewer carePlanId={carePlan.id} patientId={patientId} orientation="horizontal" detailMode="popover" size={size}><StepList />{printDialogOpen && <CarePlanPrintDialog open={printDialogOpen} onOpenChange={onOpenChange} onConfirm={handlePrintConfirm} isPrinting={isPrinting} />}</PatientCarePlanViewer>}</div>;
    $[10] = carePlan.id;
    $[11] = handlePrintConfirm;
    $[12] = isPrinting;
    $[13] = onOpenChange;
    $[14] = patientId;
    $[15] = printDialogOpen;
    $[16] = size;
    $[17] = steps.length;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== carePlan || $[20] !== handlePrint || $[21] !== orientation || $[22] !== patientId || $[23] !== size || $[24] !== t11) {
    t12 = <ReadOnlyCarePlanCard carePlan={carePlan} patientId={patientId} orientation={orientation} size={size} onPrint={handlePrint}>{t11}</ReadOnlyCarePlanCard>;
    $[19] = carePlan;
    $[20] = handlePrint;
    $[21] = orientation;
    $[22] = patientId;
    $[23] = size;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
}
function _temp(s) {
  return s.status === "PENDING" || s.status === "SCHEDULED";
}